import { z } from 'zod';

export const emailSchema = z.email({ error: 'Adresse email invalide.' });

export const passwordSchema = z.string().min(8, {
  error: 'Le mot de passe doit contenir au moins 8 caractères.',
});

export const loginPasswordSchema = z.string().min(1, {
  error: 'Le mot de passe est requis.',
});

export const loginSchema = z.object({
  email: emailSchema,
  password: loginPasswordSchema,
});

export const registerSchema = z.object({
  email: emailSchema,
  name: z.string().min(2, { error: 'Le nom doit contenir au moins 2 caractères.' }).max(100, {
    error: 'Le nom doit contenir au maximum 100 caractères.',
  }),
  password: passwordSchema,
});

export const userRoleSchema = z.enum(['admin', 'user']);
export const userStatusSchema = z.enum(['Actif', 'En attente', 'Inactif']);

export const userSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().email(),
  role: userRoleSchema,
  status: userStatusSchema,
  regionId: z.string().uuid().optional().nullable(),
  activatedAt: z.coerce.date().optional().nullable(),
  deactivatedAt: z.coerce.date().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type User = z.infer<typeof userSchema>;
export type UserStatus = z.infer<typeof userStatusSchema>;

export const adminCreateUserSchema = z.object({
  email: emailSchema,
  name: z.string().min(2, { error: 'Le nom doit contenir au moins 2 caractères.' }).max(100, {
    error: 'Le nom doit contenir au maximum 100 caractères.',
  }),
  role: userRoleSchema.default('user'),
  regionId: z.string().uuid().optional(),
  password: z.string().optional(),
});

export const adminChangeUserRoleSchema = z.object({
  role: userRoleSchema,
  userId: z.string().min(1, {
    error: 'L identifiant utilisateur est requis.',
  }),
});

export const adminSendActivationEmailSchema = z.object({
  callbackURL: z.url({
    error: 'L URL de retour est invalide.',
  }),
  email: emailSchema,
});

export const requestPasswordResetSchema = z.object({
  email: emailSchema,
});

export const resetPasswordSchema = z.object({
  newPassword: passwordSchema,
});

export const updateProfileSchema = z.object({
  image: z.string().trim().max(2048, {
    error: 'L URL de l avatar est trop longue.',
  }).superRefine((value: string, ctx: z.RefinementCtx) => {
    if (!value) {
      return;
    }

    const result = z.url({ error: 'L URL de l avatar est invalide.' }).safeParse(value);

    if (!result.success) {
      ctx.addIssue({
        code: 'custom',
        message: 'L URL de l avatar est invalide.',
      });
    }
  }).transform((value: string) => value || null).optional(),
  name: z.string().min(2, { error: 'Le nom doit contenir au moins 2 caractères.' }).max(100, {
    error: 'Le nom doit contenir au maximum 100 caractères.',
  }),
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, {
    error: 'Le mot de passe actuel est requis.',
  }),
  newPassword: passwordSchema,
  revokeOtherSessions: z.boolean().default(true),
});

export type LoginDTO = z.infer<typeof loginSchema>;
export type RegisterDTO = z.infer<typeof registerSchema>;
export type AdminCreateUserDTO = z.infer<typeof adminCreateUserSchema>;
export type AdminChangeUserRoleDTO = z.infer<typeof adminChangeUserRoleSchema>;
export type AdminSendActivationEmailDTO = z.infer<typeof adminSendActivationEmailSchema>;
export type RequestPasswordResetDTO = z.infer<typeof requestPasswordResetSchema>;
export type ResetPasswordDTO = z.infer<typeof resetPasswordSchema>;
export type UpdateProfileDTO = z.infer<typeof updateProfileSchema>;
export type ChangePasswordDTO = z.infer<typeof changePasswordSchema>;
